import React, { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { BoltIcon, SpinnerIcon } from "@/components/icons";
import { buildAdComparison, useMonthAdAnalytics, useWindowAdAnalytics } from "@/domains/Ads/hooks/useMonthAdAnalytics";
import type { AdAnalyticsSummary, MonthAdCampaign } from "@/domains/Ads/types";
import type { AdMetricChange } from "@/domains/Ads/hooks/useMonthAdAnalytics";

import {
  formatPoints,
  isAfterDay,
  metricChange,
  pointsChange,
  proRataBaseline,
  proRataFor,
  proRataLabel,
} from "../utils/metricChange";
import { campaignStack, stackEdges, type CampaignStack } from "../utils/campaignStack";
import {
  addDays,
  axisTicks,
  dayIndex,
  formatRange,
  formatRanges,
  formatShortDay,
  monthRange,
  rangeDays,
  rangeLength,
  type DayRange,
} from "../utils/dayAxis";
import {
  campaignEvents,
  campaignLanes,
  campaignsByDay,
  markBoosted,
  type CampaignSpan,
  type PostDay,
} from "../utils/dayMarkers";
import {
  ChangePill,
  ReportHeadline,
  ReportPanel,
  ReportPanelEmpty,
  ReportPanelNote,
  ReportStat,
  ReportStats,
} from "./ReportPanel";
import { ChartBox, QuietList, SOURCE_COLOR, SourceBadge, Sparkline, VizHeading } from "./ReportViz";
import { StackLegend, StackTooltip } from "./CampaignStackParts";
import { DayPips, dayPips, type DayPip } from "./DayPips";
import type { ReportPeriod, WindowPeriod } from "../utils/period";

interface AdPerformanceSectionProps {
  clientId: string;
  /** A month, or the last 7 or 28 days. */
  period: ReportPeriod;
  /** Today in Rome: a window's request keys on it. */
  today: string;
  /** The period's social posts by day, pips on the spend chart. */
  postDays?: ReadonlyArray<PostDay>;
}

interface MonthAdPerformanceProps {
  clientId: string;
  year: number;
  month: number;
  postDays?: ReadonlyArray<PostDay>;
}

/** True once the month's last day is behind today. */
const monthIsOver = (year: number, month: number): boolean => {
  const pad = (n: number) => String(n).padStart(2, "0");
  // Day 0 of the next month is this month's last day.
  return isAfterDay(`${year}-${pad(month)}-${pad(new Date(year, month, 0).getDate())}`);
};

const icon = (
  <SourceBadge color={SOURCE_COLOR.paid}>
    <BoltIcon className="w-3.5 h-3.5" />
  </SourceBadge>
);

/** Meta ad performance for /reporting, for a month or a window. */
export const AdPerformanceSection: React.FC<AdPerformanceSectionProps> = ({ clientId, period, today, postDays }) =>
  period.kind === "month" ? (
    <MonthAdPerformance clientId={clientId} year={period.year} month={period.month} postDays={postDays} />
  ) : (
    <WindowAdPerformance clientId={clientId} period={period.period} today={today} postDays={postDays} />
  );

/** Month-scoped Meta ad performance: led by spend per day, then spend,
 * link clicks and CTR against the month before, with impressions, CPM and
 * CPC demoted to a quiet list. Folded, spend and its daily sparkline.
 *
 * Never hidden: months without ad activity (and error states) render
 * the panel with an explanatory empty state instead.
 */
const MonthAdPerformance: React.FC<MonthAdPerformanceProps> = ({
  clientId,
  year,
  month,
  postDays,
}) => {
  const { current, previous, comparison: wholeMonths, isLoading, isError } =
    useMonthAdAnalytics(clientId, year, month);
  // A month in progress the ad rows cover only in part sets spend,
  // impressions and clicks against as many days of the month before, from
  // the ads' own coverage. A month that is over always compares whole
  // months; one in progress with no coverage shows no change rather than a
  // part against a whole.
  const over = monthIsOver(year, month);
  const coveredThrough = current?.coveredThrough ?? null;
  const proRata = over ? null : proRataFor(coveredThrough, year, month);
  const comparison =
    !current || !previous
      ? null
      : proRata
      ? buildAdComparison(current, previous, (value, decimals) =>
          proRataBaseline(value, proRata.share, decimals),
        )
      : over || coveredThrough
      ? wholeMonths
      : null;

  if (isLoading) {
    return (
      <ReportPanel title="Ad performance" icon={icon} collapsedBody={<LoadingRow />}>
        <LoadingRow />
      </ReportPanel>
    );
  }

  if (isError || !current) {
    return (
      <ReportPanel
        title="Ad performance"
        icon={icon}
        collapsedBody={<ReportPanelNote>Ad data is unavailable.</ReportPanelNote>}
      >
        <ReportPanelEmpty
          message="Ad data is unavailable."
          hint="Could not load ad analytics for this month — check the Meta Ads connection."
        />
      </ReportPanel>
    );
  }

  const t = current.totals;
  const range = monthRange(year, month);
  const campaignsRan = current.campaigns ?? [];
  const spans = campaignsRan.map(metaSpan);
  // Spend per day: by campaign from Meta's daily rows when the month has
  // them, else the one line.
  const stack = campaignStack(
    campaignsRan.map((c) => ({
      key: c.id,
      name: c.name,
      startDate: c.startDate,
      daily: c.daily.map((d) => ({ date: d.date, value: d.spend })),
    })),
    { range },
  );
  const spendByDay = stack ? stack.rows.map((r) => r.total) : trendByDay(current.trend, range);
  const lastSpendDay = spendByDay.reduce<number>((last, v, i) => (v != null ? i + 1 : last), 0);
  const boostedDays = markBoosted(postDays ?? [], campaignsRan);
  const pips = dayPips(boostedDays, campaignsByDay(campaignEvents(spans), range));
  // Folded: the campaigns' days under the sparkline, the biggest spenders
  // first, and a tick on each post day, amber when a post there was boosted.
  const bySpend = [...campaignsRan].sort((a, b) => spentIn(b) - spentIn(a)).map(metaSpan);
  const sparkSpans = campaignLanes(bySpend, range, addDays(range.since, lastSpendDay - 1)).lanes.map(
    (lane) => ({ key: lane.key, from: lane.from, to: lane.to }),
  );
  const sparkTicks = boostedDays.flatMap((d) => {
    const at = dayIndex(range, d.date);
    return at == null
      ? []
      : [{ key: d.date, at, color: d.posts.some((p) => p.boost) ? SOURCE_COLOR.paid : SOURCE_COLOR.social }];
  });
  const hadActivity =
    t.impressions > 0 ||
    t.clicks > 0 ||
    t.spend > 0 ||
    (previous?.totals.spend ?? 0) > 0;

  if (!hadActivity) {
    return (
      <ReportPanel
        title="Ad performance"
        icon={icon}
        collapsedBody={<ReportPanelNote>No ad activity this month.</ReportPanelNote>}
      >
        <ReportPanelEmpty
          message="No ad activity this month."
          hint="Spend, impressions and clicks show here once a campaign runs."
        />
      </ReportPanel>
    );
  }

  const currency = "EUR";
  const fmtMoney = makeCurrencyFmt(currency);
  const fmtMoneyPrecise = (n: number) =>
    n >= 1
      ? fmtMoney.format(n)
      : new Intl.NumberFormat(undefined, {
          style: "currency",
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(n);

  const campaigns =
    current.campaignCount > 0
      ? `${current.campaignCount} campaign${current.campaignCount === 1 ? "" : "s"} active`
      : "No active campaigns this month";
  const headerSubtitle =
    proRata && comparison ? `${campaigns} · ${proRataLabel(proRata)}` : campaigns;

  const ctrChange = comparison ? pointsChange(t.ctr, previous?.totals.ctr) : undefined;
  // A money delta keeps its cents and its euro sign: "+€14.40".
  const fmtMoneyDelta = (n: number) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  const pill = (
    change: AdMetricChange | null | undefined,
    formatChange?: (value: number) => string,
    trend?: "up" | "down" | "neutral",
  ) =>
    change ? <ChangePill change={change} formatChange={formatChange} trend={trend} /> : undefined;

  return (
    <ReportPanel
      title="Ad performance"
      icon={icon}
      summary={headerSubtitle}
      headlineLabel="Spend"
      collapsedBody={
        <ReportHeadline
          stat={
            <ReportStat
              size="lg"
              label="Spend"
              value={fmtMoney.format(t.spend)}
              change={comparison?.spend}
              formatChange={fmtMoneyDelta}
            />
          }
          chart={
            <Sparkline
              values={spendByDay.slice(0, lastSpendDay)}
              color={SOURCE_COLOR.paid}
              title="Spend per day this month"
              spans={sparkSpans}
              ticks={sparkTicks}
            />
          }
        />
      }
    >
      <div className="space-y-5">
        {(stack || current.trend.length > 1) && (
          <SpendChart trend={current.trend} stack={stack} fmtMoney={fmtMoney} range={range} pips={pips} />
        )}
        <ReportStats>
          <ReportStat
            label="Spend"
            value={fmtMoney.format(t.spend)}
            change={comparison?.spend}
            formatChange={fmtMoneyDelta}
          />
          <ReportStat label="Link clicks" value={formatCompactInt(t.clicks)} change={comparison?.clicks} />
          {/* A ratio's move reads in points, as on the Google Ads CTR row. */}
          <ReportStat
            label="CTR"
            value={t.ctr != null ? `${(t.ctr * 100).toFixed(2)}%` : "—"}
            change={ctrChange}
            formatChange={formatPoints}
          />
        </ReportStats>
        <QuietList
          rows={[
            {
              label: "Impressions",
              value: formatCompactInt(t.impressions),
              aside: pill(comparison?.impressions),
            },
            {
              label: "CPM",
              value: t.cpm != null ? fmtMoneyPrecise(t.cpm) : "—",
              aside: pill(comparison?.cpm, fmtMoneyDelta, favorabilityTrend(comparison?.cpm)),
            },
            {
              label: "CPC",
              value: t.cpc != null ? fmtMoneyPrecise(t.cpc) : "—",
              aside: pill(comparison?.cpc, fmtMoneyDelta, favorabilityTrend(comparison?.cpc)),
            },
          ]}
        />
      </div>
    </ReportPanel>
  );
};

/** Meta ad performance over a window, from ad_daily_spend: spend against
 * the days before (none when they start before the daily data does), the
 * spend line over the span, a day without delivery at €0, and the campaign
 * stack. Impressions, link clicks and the rates show only once they are
 * stored per day; until then the panel says they read by month. A window
 * without a campaign is the empty state, never a flat €0 line. The pro-rata
 * comparison and monthIsOver are Month's only.
 */
const WindowAdPerformance: React.FC<{
  clientId: string;
  period: WindowPeriod;
  today: string;
  postDays?: ReadonlyArray<PostDay>;
}> = ({ clientId, period, today, postDays }) => {
  const { data, isLoading, isError } = useWindowAdAnalytics(clientId, period, today);

  if (isLoading) {
    return (
      <ReportPanel title="Ad performance" icon={icon} collapsedBody={<LoadingRow />}>
        <LoadingRow />
      </ReportPanel>
    );
  }
  if (isError || !data) {
    return (
      <ReportPanel
        title="Ad performance"
        icon={icon}
        collapsedBody={<ReportPanelNote>Ad data is unavailable.</ReportPanelNote>}
      >
        <ReportPanelEmpty
          message="Ad data is unavailable."
          hint="Could not load ad analytics for these days — check the Meta Ads connection."
        />
      </ReportPanel>
    );
  }
  const range: DayRange = {
    since: data.span.since < data.dataFrom ? data.dataFrom : data.span.since,
    until: data.span.until,
  };
  const days = data.compares
    ? formatRanges(range, data.previousSpan)
    : `${formatRange(range)} · no comparison: data starts ${formatShortDay(data.dataFrom)}`;
  if (data.campaignCount === 0) {
    return (
      <ReportPanel
        title="Ad performance"
        icon={icon}
        summary={days}
        collapsedBody={<ReportPanelNote>No ad activity in these days.</ReportPanelNote>}
      >
        <ReportPanelEmpty
          message="No ad activity in these days."
          hint="Spend shows here once a campaign runs."
        />
      </ReportPanel>
    );
  }

  const t = data.totals;
  const before = data.compares ? data.previousTotals : null;
  const campaignsRan = data.campaigns;
  const stack = campaignStack(
    campaignsRan.map((c) => ({
      key: c.id,
      name: c.name,
      startDate: c.startDate,
      daily: c.daily.map((d) => ({ date: d.date, value: d.spend })),
    })),
    { range },
  );
  // Inside the sync's coverage a day without a row is a day without delivery, so the trend has every day.
  const spendByDay = trendByDay(data.trend, range);
  const boostedDays = markBoosted(postDays ?? [], campaignsRan);
  const pips = dayPips(boostedDays, campaignsByDay(campaignEvents(campaignsRan.map(metaSpan)), range));
  const bySpend = [...campaignsRan].sort((a, b) => spentIn(b) - spentIn(a)).map(metaSpan);
  const sparkSpans = campaignLanes(bySpend, range, range.until).lanes.map((lane) => ({
    key: lane.key,
    from: lane.from,
    to: lane.to,
  }));
  const sparkTicks = boostedDays.flatMap((d) => {
    const at = dayIndex(range, d.date);
    return at == null
      ? []
      : [{ key: d.date, at, color: d.posts.some((p) => p.boost) ? SOURCE_COLOR.paid : SOURCE_COLOR.social }];
  });

  const fmtMoney = makeCurrencyFmt("EUR");
  const fmtPrecise = (n: number) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  const fmtMoneyPrecise = (n: number) => (n >= 1 ? fmtMoney.format(n) : fmtPrecise(n));
  const spendChange = metricChange(t.spend, before?.spend);
  const pill = (change: ReturnType<typeof metricChange>, formatChange?: (value: number) => string, lowerWins = false) =>
    change ? (
      <ChangePill
        change={change}
        formatChange={formatChange}
        trend={lowerWins ? favorabilityTrend(change) : undefined}
      />
    ) : undefined;
  const summary = `${data.campaignCount} campaign${data.campaignCount === 1 ? "" : "s"} · ${days}`;

  return (
    <ReportPanel
      title="Ad performance"
      icon={icon}
      summary={summary}
      headlineLabel="Spend"
      collapsedBody={
        <ReportHeadline
          stat={
            <ReportStat
              size="lg"
              label="Spend"
              value={fmtMoney.format(t.spend)}
              change={spendChange}
              formatChange={fmtPrecise}
            />
          }
          chart={
            <Sparkline
              values={spendByDay}
              color={SOURCE_COLOR.paid}
              title="Spend per day"
              spans={sparkSpans}
              ticks={sparkTicks}
            />
          }
        />
      }
    >
      <div className="space-y-5">
        <SpendChart trend={data.trend} stack={stack} fmtMoney={fmtMoney} range={range} pips={pips} />
        {t.impressions != null && t.clicks != null ? (
          <>
            <ReportStats>
              <ReportStat label="Spend" value={fmtMoney.format(t.spend)} change={spendChange} formatChange={fmtPrecise} />
              <ReportStat
                label="Link clicks"
                value={formatCompactInt(t.clicks)}
                change={metricChange(t.clicks, before?.clicks)}
              />
              <ReportStat
                label="CTR"
                value={t.ctr != null ? `${(t.ctr * 100).toFixed(2)}%` : "—"}
                change={pointsChange(t.ctr, before?.ctr)}
                formatChange={formatPoints}
              />
            </ReportStats>
            <QuietList
              rows={[
                {
                  label: "Impressions",
                  value: formatCompactInt(t.impressions),
                  aside: pill(metricChange(t.impressions, before?.impressions)),
                },
                {
                  label: "CPM",
                  value: t.cpm != null ? fmtMoneyPrecise(t.cpm) : "—",
                  aside: pill(metricChange(t.cpm, before?.cpm), fmtPrecise, true),
                },
                {
                  label: "CPC",
                  value: t.cpc != null ? fmtMoneyPrecise(t.cpc) : "—",
                  aside: pill(metricChange(t.cpc, before?.cpc), fmtPrecise, true),
                },
              ]}
            />
          </>
        ) : (
          <>
            <ReportStats>
              <ReportStat label="Spend" value={fmtMoney.format(t.spend)} change={spendChange} formatChange={fmtPrecise} />
            </ReportStats>
            <ReportPanelNote>
              Impressions, link clicks, CTR, CPM and CPC read by month until daily ad data lands.
            </ReportPanelNote>
          </>
        )}
      </div>
    </ReportPanel>
  );
};

/** A pill takes its tone from the sign of `change.value`, which is right
 *  for additive metrics but wrong for CPC/CPM where *lower* is the win.
 *  Override the trend explicitly so the colour reflects favorability (a
 *  CPM drop is green) while the label still shows the raw, honest delta
 *  (e.g. "-€0.02"). */
const favorabilityTrend = (
  c: { value: number } | null | undefined,
): "up" | "down" | "neutral" | undefined => {
  if (!c) return undefined;
  if (c.value < 0) return "up";
  if (c.value > 0) return "down";
  return "neutral";
};

const TICK = { fontSize: 10, fill: "#9CA3AF" };
// The pips sit on the plot's x axis line, so they read these two.
const Y_AXIS_WIDTH = 36;
const X_AXIS_HEIGHT = 30;

/** A Meta campaign as the day markers read it. */
const metaSpan = (c: MonthAdCampaign): CampaignSpan => ({
  key: c.id,
  name: c.name,
  startDate: c.startDate,
  endDate: c.endDate,
  days: c.daily.map((d) => d.date),
  channelType: null,
  platform: c.platform,
});

const spentIn = (c: MonthAdCampaign) => c.daily.reduce((sum, d) => sum + d.spend, 0);

/** The trend by day of the range, a day without a row a gap. */
const trendByDay = (trend: AdAnalyticsSummary["trend"], range: DayRange): Array<number | null> => {
  const byDate = new Map(trend.map((d) => [d.date, d.spend]));
  return rangeDays(range).map((date) => byDate.get(date) ?? null);
};

/** The panel's hero: spend per day on the range's days, so a day without
 *  spend keeps its place and a post's pip lands on its day. With Meta's
 *  daily rows it stacks the campaigns, oldest at the bottom, the stack's top
 *  the day's spend; without them, one amber line over a 10% wash. */
const SpendChart: React.FC<{
  trend: AdAnalyticsSummary["trend"];
  stack: CampaignStack | null;
  fmtMoney: Intl.NumberFormat;
  range: DayRange;
  pips: ReadonlyArray<DayPip>;
}> = ({ trend, stack, fmtMoney, range, pips }) => {
  const trendRows = useMemo(
    () =>
      trend.flatMap((d) => {
        const index = dayIndex(range, d.date);
        return index == null ? [] : [{ ...d, index }];
      }),
    [trend, range],
  );
  const length = rangeLength(range);
  const fmtPrecise = (n: number) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  return (
    <div>
      <VizHeading>Spend per day</VizHeading>
      <ChartBox height={168} className="relative">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={stack ? stackEdges(stack.rows) : trendRows}
            margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
          >
            <CartesianGrid vertical={false} stroke="#F3F4F6" />
            <XAxis
              dataKey="index"
              type="number"
              domain={[0, length - 1]}
              ticks={axisTicks(length)}
              tickFormatter={(index: number) => formatShortDay(addDays(range.since, index))}
              height={X_AXIS_HEIGHT}
              tick={TICK}
              tickLine={false}
              axisLine={{ stroke: "#E5E7EB" }}
            />
            <YAxis
              width={Y_AXIS_WIDTH}
              tickCount={3}
              tick={TICK}
              tickLine={false}
              axisLine={false}
              tickFormatter={(n: number) => fmtMoney.format(n)}
            />
            <RechartsTooltip
              cursor={{ stroke: "#E5E7EB" }}
              content={({ active, payload, label }) => {
                if (!active) return null;
                if (stack) {
                  const row = stack.rows[Number(label)];
                  return row && row.total != null ? (
                    <StackTooltip
                      row={row}
                      layers={stack.layers}
                      format={fmtPrecise}
                      heading={formatShortDay(row.date)}
                      totalLabel="Spend"
                    />
                  ) : null;
                }
                return payload && payload[0] ? (
                  <div className="rounded-md bg-gray-900 text-white text-[11px] px-2 py-1 shadow-md">
                    <div className="font-medium tabular-nums">
                      {fmtMoney.format(Number(payload[0].value) || 0)}
                    </div>
                    <div className="text-gray-300">{String(payload[0].payload?.date)}</div>
                  </div>
                ) : null;
              }}
            />
            {stack ? (
              stack.layers.map((layer) => (
                <Area
                  key={layer.key}
                  dataKey={(row: CampaignStack["rows"][number]) => row.values[layer.key]}
                  name={layer.name}
                  stackId="campaigns"
                  type="linear"
                  stroke="#FFFFFF"
                  strokeWidth={1.5}
                  fill={layer.color}
                  fillOpacity={1}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                />
              ))
            ) : (
              <Area
                type="monotone"
                dataKey="spend"
                stroke={SOURCE_COLOR.paid}
                strokeWidth={2}
                fill={SOURCE_COLOR.paid}
                fillOpacity={0.1}
                dot={false}
                activeDot={{ r: 4, stroke: "#FFFFFF", strokeWidth: 2 }}
                isAnimationActive={false}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
        {pips.length > 0 && (
          <DayPips
            pips={pips}
            range={range}
            domain={[0, length - 1]}
            inset={{ left: Y_AXIS_WIDTH, right: 4, bottom: X_AXIS_HEIGHT }}
          />
        )}
      </ChartBox>
      {stack && <StackLegend layers={stack.layers} format={fmtPrecise} />}
    </div>
  );
};

const LoadingRow: React.FC = () => (
  <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
    <SpinnerIcon className="w-4 h-4" />
    Loading ad performance…
  </div>
);

const formatCompactInt = (n: number): string => {
  if (n === 0) return "0";
  if (n < 1_000) return n.toLocaleString();
  if (n < 10_000) return n.toLocaleString();
  if (n < 1_000_000) return `${(n / 1_000).toFixed(n < 100_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
};

const makeCurrencyFmt = (currency: string): Intl.NumberFormat =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  });
